import React, { useState } from "react";
import MainLayout from "../layouts/MainLayout";
import Button from "../components/Button";
import Card from "../components/Card";
import WaveDivider from "../components/WaveDivider";

const LandingPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = [
    {
      question: "What formats can I export to?",
      answer:
        "You can export your parsed data to CSV, Excel, or JSON for immediate use in analytics, accounting, or internal tools.",
    },
    {
      question: "Do you store my bank statements?",
      answer:
        "No. All files are processed in memory and deleted immediately after conversion to ensure privacy and security.",
    },
    {
      question: "Which banks are supported?",
      answer:
        "The converter works with most major banks worldwide and continually adds new formats to ensure compatibility.",
    },
    {
      question: "How accurate is the extraction?",
      answer:
        "Our parser is optimized for complex layouts and has been trained on thousands of statements for high accuracy.",
    },
    {
      question: "Do you offer an API?",
      answer:
        "Yes. You can integrate programmatic PDF to CSV/Excel/JSON conversion into your own systems via our API.",
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <MainLayout>
      <main className="min-h-screen bg-[#F6E1A8] text-[#111827]">
        {/* Hero */}
        <section
          id="top"
          className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-[#F6E1A8]"
        >
          <div className="max-w-4xl mx-auto">
            <div className="inline-block mb-6 px-4 py-2 bg-[#C994FF] rounded-full text-[#000000] text-sm font-medium">
              Instant Conversion
            </div>

            <h1 className="text-6xl md:text-7xl font-extrabold mb-6 leading-tight text-[#111827]">
              Turn Any Bank Statement Into Clean, Usable Data in Seconds
            </h1>

            <p className="text-lg md:text-xl text-[#111827]/80 mb-8">
              Upload your PDF and instantly get accurate CSV, Excel, or JSON without manual formatting.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button>Convert a Statement Instantly</Button>
              <div className="text-sm text-[#111827]/60">
                No data stored · Secure processing
              </div>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-[#111827]/80">
              <span className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white">
                  ✓
                </span>
                Used by founders, accountants, and finance teams
              </span>

              <span className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white">
                  ✓
                </span>
                Accurate extraction across multiple bank formats
              </span>

              <span className="flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-green-500 text-white">
                  ✓
                </span>
                Designed for fast, secure, high-volume processing
              </span>
            </div>
          </div>
        </section>

        {/* Wave */}
        <WaveDivider color="#45AD94" />

        {/* Why Section */}
        <section id="why" className="py-32 px-6 bg-[#45AD94] scroll-mt-20">
          <div className="max-w-6xl mx-auto text-center text-[#FFFFFF]">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why This Converter
            </h2>

            <p className="text-xl text-[#FFFFFF]/90 mb-16">
              Make financial data extraction simple, reliable, and automated.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: "/icons/pdf.svg",
                  title: "Processes PDFs Reliably",
                  desc: "Reads complex layouts with consistent accuracy.",
                  bg: "bg-[#FFE37E]",
                },
                {
                  icon: "/icons/data.svg",
                  title: "Clean, Structured Output",
                  desc: "Normalized columns, clean dates, readable descriptions.",
                  bg: "bg-[#9EDCF5]",
                },
                {
                  icon: "/icons/secure.svg",
                  title: "Secure by Design",
                  desc: "Files are processed in session then deleted immediately.",
                  bg: "bg-[#FEBBCE]",
                },
                {
                  icon: "/icons/bank.svg",
                  title: "Supports Many Banks",
                  desc: "Works with local and international statement formats.",
                  bg: "bg-[#C6F6D5]",
                },
                {
                  icon: "/icons/export.svg",
                  title: "Flexible Export Options",
                  desc: "Download CSV, Excel, or JSON in one click.",
                  bg: "bg-[#FDE68A]",
                },
                {
                  icon: "/icons/update.svg",
                  title: "Constantly Improving",
                  desc: "Updated with new parsers and smarter extraction.",
                  bg: "bg-[#FBCFE8]",
                },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="card border border-white/20 shadow-lg bg-white text-[#111827] p-8 rounded-xl"
                >
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-20 h-20 rounded-full flex items-center justify-center mb-5 ${feature.bg}`}
                    >
                      <img
                        src={feature.icon}
                        alt={feature.title}
                        className="w-10 h-10"
                      />
                    </div>

                    <h3 className="text-xl font-semibold mb-2">
                      {feature.title}
                    </h3>

                    <p className="text-[#111827]/70">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12">
              <Button>View Full Features</Button>
            </div>
          </div>
        </section>

        {/* What's Inside */}
        <section
          id="inside"
          className="py-32 px-6 bg-[#FFFBEB] text-[#111827] scroll-mt-20"
        >
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              What’s Inside
            </h2>
            <p className="text-xl mb-16">
              Everything you need for accurate and fast bank statement conversion.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-left">
              {[
                "PDF Parsing",
                "Multi-bank Support",
                "Clean CSV Export",
                "Excel Export",
                "JSON Export",
                "Transaction Normalization",
                "Categorization",
                "Duplicate Detection",
                "Developer API Access",
              ].map((title, i) => (
                <Card key={i}>
                  <div>
                    <h3 className="text-xl font-semibold mb-2 text-[#111827]">
                      {title}
                    </h3>
                    <p className="text-[#111827]/70">
                      Essential feature included out of the box.
                    </p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12">
              <Button>View Documentation</Button>
            </div>
          </div>
        </section>

        {/* Pricing */}
        <section
          id="pricing"
          className="py-32 px-6 bg-[#FFE711] text-[#111827] scroll-mt-20"
        >
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Pricing</h2>
            <p className="text-xl mb-16">
              Choose the plan that fits your workflow.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  name: "Starter",
                  desc: "Best for individuals needing occasional conversions.",
                },
                {
                  name: "Pro",
                  desc: "Designed for businesses handling frequent statements.",
                },
                {
                  name: "Team / Extended",
                  desc: "For accounting teams, startups, and operations that require high-volume processing.",
                },
                {
                  name: "Enterprise",
                  desc: "Includes API access, custom integrations, and priority support.",
                },
              ].map((plan, i) => (
                <Card key={i}>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{plan.name}</h3>
                    <p className="text-[#111827]/70">{plan.desc}</p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="mt-12">
              <Button>Get Started Now</Button>
            </div>
          </div>
        </section>

        {/* Used By Section */}
        <section id="usedby" className="py-32 px-6 bg-[#FF94E7]">
          <div className="max-w-6xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-[#111827]">
              Trusted in Real Financial Workflows
            </h2>
            <p className="text-xl text-[#111827]/70 mb-16">
              Used by founders, accountants, auditors, and fintech builders who need reliable transaction data.
            </p>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-10">
              {[
                {
                  name: "LedgerClear",
                  desc: "Automated bookkeeping workflows.",
                  logo: "/logos/ledgerclear.png",
                  link: "#",
                },
                {
                  name: "FinSight",
                  desc: "Financial analytics for small businesses.",
                  logo: "/logos/finsight.png",
                  link: "#",
                },
                {
                  name: "AutoBooks AI",
                  desc: "Automated transaction categorization.",
                  logo: "/logos/autobooks.png",
                  link: "#",
                },
                {
                  name: "CreditPath",
                  desc: "Underwriting and scoring tools.",
                  logo: "/logos/creditpath.png",
                  link: "#",
                },
              ].map((project, i) => (
                <Card key={i}>
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex flex-col items-center text-center rounded-2xl p-8 hover:-translate-y-1 transition-all"
                  >
                    <div className="w-20 h-20 mb-6 flex items-center justify-center bg-[#2A2A2A] rounded-full">
                      <img
                        src={project.logo}
                        alt={project.name}
                        className="w-10 h-10 object-contain"
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-[#111827] mb-2">
                      {project.name}
                    </h3>
                    <p className="text-[#111827]/70 text-sm">{project.desc}</p>
                  </a>
                </Card>
              ))}
            </div>

            <div className="mt-12">
              <Button>Submit Your Project</Button>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section
          id="faq"
          className="py-32 px-6 bg-[#F6E1A8] text-[#111827] scroll-mt-20"
        >
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-center mb-16">
              Got questions? We've got answers.
            </p>

            <div className="space-y-4">
              {faqItems.map((item, index) => (
                <div key={index} className="bg-white p-4 rounded-xl shadow-lg">
                  <button
                    className="w-full flex justify-between items-center text-left"
                    onClick={() => toggleFAQ(index)}
                  >
                    <span className="text-xl font-semibold text-[#111827]">
                      {item.question}
                    </span>
                    <svg
                      className={`w-6 h-6 transform transition-transform duration-300 ${
                        openIndex === index ? "rotate-180" : "rotate-0"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  <div
                    className={`overflow-hidden transition-all duration-500 ease-in-out ${
                      openIndex === index
                        ? "max-h-96 opacity-100 pt-4"
                        : "max-h-0 opacity-0"
                    }`}
                  >
                    <p className="text-[#111827]/70">{item.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 px-6 text-center bg-[#FFFBEB] text-[#000000]">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Convert Your First Statement in Seconds
            </h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Fast, reliable, and secure conversion built for real financial workflows.
            </p>
            <Button>Start Converting Now</Button>
          </div>
        </section>
      </main>
    </MainLayout>
  );
};

export default LandingPage;
